/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise */

var extendStatics = function(d, b) {
    extendStatics = Object.setPrototypeOf ||
        ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
        function (d, b) { for (var p in b) if (Object.prototype.hasOwnProperty.call(b, p)) d[p] = b[p]; };
    return extendStatics(d, b);
};

function __extends(d, b) {
    if (typeof b !== "function" && b !== null)
        throw new TypeError("Class extends value " + String(b) + " is not a constructor or null");
    extendStatics(d, b);
    function __() { this.constructor = d; }
    d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
}

var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

function __spreadArray(to, from) {
    for (var i = 0, il = from.length, j = to.length; i < il; i++, j++)
        to[j] = from[i];
    return to;
}

var BoxDBError = /** @class */ (function (_super) {
    __extends(BoxDBError, _super);
    function BoxDBError(message) {
        var _newTarget = this.constructor;
        var _this = _super.call(this, message) || this;
        _this.message = message;
        _this.name = _newTarget.name;
        Object.setPrototypeOf(_this, _newTarget.prototype);
        return _this;
    }
    return BoxDBError;
}(Error));

var TransactionMode;
(function (TransactionMode) {
    TransactionMode["READ"] = "readonly";
    TransactionMode["WRITE"] = "readwrite";
})(TransactionMode || (TransactionMode = {}));
var TransactionType;
(function (TransactionType) {
    TransactionType["ADD"] = "add";
    TransactionType["GET"] = "get";
    TransactionType["PUT"] = "put";
    TransactionType["DELETE"] = "delete";
    TransactionType["CLEAR"] = "clear";
    TransactionType["COUNT"] = "count";
    // $ prefix means this task is using cursor
    TransactionType["$GET"] = "$get";
    TransactionType["$UPDATE"] = "$update";
    TransactionType["$DELETE"] = "$delete";
    TransactionType["INTERRUPT"] = "interrupt";
})(TransactionType || (TransactionType = {}));
var BoxDataTypes;
(function (BoxDataTypes) {
    BoxDataTypes["BOOLEAN"] = "1";
    BoxDataTypes["NUMBER"] = "2";
    BoxDataTypes["STRING"] = "3";
    BoxDataTypes["DATE"] = "4";
    BoxDataTypes["ARRAY"] = "5";
    BoxDataTypes["OBJECT"] = "6";
    BoxDataTypes["REGEXP"] = "7";
    BoxDataTypes["FILE"] = "8";
    BoxDataTypes["BLOB"] = "9";
    BoxDataTypes["ANY"] = "0";
})(BoxDataTypes || (BoxDataTypes = {}));
var BoxCursorDirections;
(function (BoxCursorDirections) {
    BoxCursorDirections["ASC"] = "next";
    BoxCursorDirections["ASC_UNIQUE"] = "nextunique";
    BoxCursorDirections["DESC"] = "prev";
    BoxCursorDirections["DESC_UNIQUE"] = "prevunique";
})(BoxCursorDirections || (BoxCursorDirections = {}));

var READONLY_TYPES = [
    TransactionType.GET,
    TransactionType.COUNT,
    TransactionType.$GET,
    TransactionType.INTERRUPT,
];
var HAS_VALUE_TYPES = [
    TransactionType.GET,
    TransactionType.$GET,
    TransactionType.COUNT,
    TransactionType.ADD,
];
var BoxTransaction = /** @class */ (function () {
    function BoxTransaction() {
        this.idb = { value: null };
    }
    /**
     * Store reference of idb instance
     *
     * @param idb IDBDatabase
     */
    BoxTransaction.prototype.init = function (idb) {
        this.idb.value = idb;
    };
    /**
     * Clear IDB instance
     */
    BoxTransaction.prototype.close = function () {
        this.idb.value = null;
    };
    /**
     * Execute all tasks
     *
     * @param tasks Transaction tasks
     */
    BoxTransaction.prototype.run = function () {
        var _this = this;
        var tasks = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            tasks[_i] = arguments[_i];
        }
        var db = this.idb.value;
        if (db === null) {
            throw new BoxDBError('Database not ready');
        }
        var firstTaskType = tasks[0].type;
        var needResponse = tasks.length === 1 && HAS_VALUE_TYPES.includes(firstTaskType);
        var res = null;
        // Get object store names from tasks
        var storeNamesInTasks = Object.keys(tasks.reduce(function (set, curr) {
            // Add object key as store name
            curr.name && (set[curr.name] = 0);
            return set;
        }, {}));
        // Select transaction mode
        var mode = tasks.some(function (task) { return !READONLY_TYPES.includes(task.type); })
            ? TransactionMode.WRITE
            : TransactionMode.READ;
        return new Promise(function (resolve, reject) {
            // Open new transaction
            var tx = db.transaction(storeNamesInTasks, mode);
            var _loop_1 = function (task) {
                var _a;
                var action = task.type;
                var args = (task.args || []).filter(function (x) { return x !== undefined; });
                if (action === TransactionType.INTERRUPT) {
                    // interrupt manually
                    tx.abort();
                }
                else {
                    var objectStore = tx.objectStore(task.name);
                    if (action === TransactionType.$GET ||
                        action === TransactionType.$UPDATE ||
                        action === TransactionType.$DELETE) {
                        // using cursor
                        _this.cursor(objectStore, task).then(function (records) { return (res = records); });
                    }
                    else {
                        // `action` = get | add | put | delete | count | clear
                        // Skip ts type checking
                        var request_1 = (_a = objectStore[action]).call.apply(_a, __spreadArray([objectStore], args));
                        request_1.onsuccess = function () { return (res = request_1.result); };
                    }
                }
            };
            // Do each tasks
            // abort transaction if error occurs during task
            for (var _i = 0, tasks_1 = tasks; _i < tasks_1.length; _i++) {
                var task = tasks_1[_i];
                _loop_1(task);
            }
            var errorHandler = function (event) {
                // Error event will bubbled up to IDB
                reject(event);
            };
            // On complete
            tx.oncomplete = function () { return resolve(needResponse ? res !== null && res !== void 0 ? res : null : void 0); };
            // On error/abort
            tx.onerror = errorHandler;
        });
    };
    /**
     * Handling cursor task with helpers
     *
     * @param objectStore Object store object
     * @param task Current task
     */
    BoxTransaction.prototype.cursor = function (objectStore, task) {
        var filter = task.filter;
        var range = task.range;
        var limit = task.limit || null;
        var direction = task.direction || 'next';
        var updateValue = task.updateValue || null;
        var res = [];
        // Bundle of filter functions
        var pass = filter && filter.length ? function (value) { return filter.every(function (f) { return f(value); }); } : function () { return true; };
        // Using IDBKeyRange + IDBCursorDirection
        var index = range && range.index;
        if (index && !objectStore.indexNames.contains(index)) {
            throw new BoxDBError(index + ' field is not an index');
        }
        return new Promise(function (resolve, reject) {
            // Counting variable for limit records
            var rows = 0;
            var running = true;
            var limitHandler = function () { return limit === null || limit > rows; };
            var cursorTaskRequestHandler = function (request) {
                /* istanbul ignore next */
                request.onerror = function (event) { return (running = !!void reject(event)); };
            };
            var request = (index ? objectStore.index(index) : objectStore).openCursor(range ? range.value : null, direction);
            request.onsuccess = function () {
                var cursor = request.result;
                if (running && cursor && limitHandler()) {
                    var record = cursor.value;
                    switch (task.type) {
                        case TransactionType.$GET:
                            pass(record) && ++rows && res.push(record);
                            break;
                        case TransactionType.$UPDATE:
                            pass(record) &&
                                cursorTaskRequestHandler(cursor.update(__assign(__assign({}, record), updateValue)));
                            break;
                        case TransactionType.$DELETE:
                            pass(record) && cursorTaskRequestHandler(cursor.delete());
                            break;
                    }
                    cursor.continue();
                }
                else {
                    resolve(res);
                }
            };
        });
    };
    return BoxTransaction;
}());

var getCursorHandler = function (context, range, filter) {
    return {
        get: function (order, limit) {
            return context.$(TransactionType.$GET, {
                direction: order,
                limit: limit,
                filter: filter,
                range: range,
            });
        },
        update: function (value) {
            context.pass(value, false);
            return context.$(TransactionType.$UPDATE, { range: range, filter: filter, updateValue: value });
        },
        delete: function () {
            return context.$(TransactionType.$DELETE, { range: range, filter: filter });
        },
    };
};
var getTransactionCursorHandler = function (context, range, filter) {
    return {
        update: function (value) {
            context.pass(value, false);
            return createTask(TransactionType.$UPDATE, context.__name, {
                range: range,
                filter: filter,
                updateValue: value,
            });
        },
        delete: function () {
            return createTask(TransactionType.$DELETE, context.__name, { range: range, filter: filter });
        },
    };
};
var createTask = function (type, name, taskArgs) {
    var _a = taskArgs || {}, args = _a.args, direction = _a.direction, filter = _a.filter, range = _a.range, limit = _a.limit, updateValue = _a.updateValue;
    return {
        type: type,
        name: name,
        args: args,
        direction: direction,
        filter: filter,
        range: range,
        limit: limit,
        updateValue: updateValue,
    };
};

/**
 * Validate with configured type
 *
 * @param type Type identifier
 * @param value Value for check
 */
var typeValidator = function (type, value) {
    if (value === null)
        return true;
    switch (type) {
        case BoxDataTypes.BOOLEAN:
            return typeof value === 'boolean';
        case BoxDataTypes.NUMBER:
            return typeof value === 'number';
        case BoxDataTypes.STRING:
            return typeof value === 'string';
        case BoxDataTypes.DATE:
            return value instanceof Date;
        case BoxDataTypes.ARRAY:
            return Array.isArray(value);
        case BoxDataTypes.OBJECT:
            return typeof value === 'object';
        case BoxDataTypes.REGEXP:
            return value instanceof RegExp;
        case BoxDataTypes.FILE:
            return value instanceof File;
        case BoxDataTypes.BLOB:
            return value instanceof Blob;
        case BoxDataTypes.ANY:
            return true; // any
    }
};
/**
 * Check data based on schema
 *
 * 1. Compare target field / schema field count
 * 2. Check target fields name in schema
 * 3. Check fields value type
 *
 * @param this Box
 * @param target Target data
 * @param strict Enable strict mode (disabled: check properties(like optinal) / enabled: +types)
 */
function schemaValidator(target, strict) {
    if (strict === void 0) { strict = true; }
    var schemaKeys = Object.keys(this.__schema);
    var targetKeys = Object.keys(target);
    // Checking in strict mode
    var samekeyLength = !strict || schemaKeys.length === targetKeys.length;
    var correctValueTypes = !strict ||
        Object.entries(this.__schema).every(function (_a) {
            var k = _a[0], v = _a[1];
            return typeValidator(typeof v === 'string' ? v : v.type, target[k]);
        });
    if (!(samekeyLength && correctValueTypes && targetKeys.every(function (k) { return schemaKeys.includes(k); }))) {
        throw new BoxDBError('Data not valid');
    }
}
/**
 * Create new box data
 *
 * @param baseObject
 * @param targetObject
 */
function createBoxData(initalData) {
    var boxData = {};
    Object.keys(this.__schema).forEach(function (k) { var _a; return (boxData[k] = (_a = (initalData && initalData[k])) !== null && _a !== void 0 ? _a : null); });
    return boxData;
}
/**
 * Execute task and returns tasked Promise
 *
 * @param type Transaction type
 * @param args Arguments
 */
function transactionExecuter(type, args) {
    return this.tx.run(createTask(type, this.__name, args));
}
// BoxHandler
var boxHandler = {
    getName: function () {
        return this.__name;
    },
    getVersion: function () {
        return this.__version;
    },
    add: function (value, key) {
        this.pass(value);
        return this.$(TransactionType.ADD, {
            args: [value, key],
        });
    },
    get: function (key) {
        return this.$(TransactionType.GET, {
            args: [key],
        });
    },
    put: function (value, key) {
        this.pass(value, false);
        return this.$(TransactionType.PUT, {
            args: [value, key],
        });
    },
    delete: function (key) {
        return this.$(TransactionType.DELETE, {
            args: [key],
        });
    },
    find: function (range) {
        var predicate = [];
        for (var _i = 1; _i < arguments.length; _i++) {
            predicate[_i - 1] = arguments[_i];
        }
        return getCursorHandler(this, range, predicate);
    },
    clear: function () {
        return this.$(TransactionType.CLEAR);
    },
    count: function () {
        return this.$(TransactionType.COUNT);
    },
};
// BoxTask
var boxTask = {
    $add: function (value, key) {
        this.pass(value);
        return createTask(TransactionType.ADD, this.__name, { args: [value, key] });
    },
    $put: function (value, key) {
        this.pass(value, false);
        return createTask(TransactionType.PUT, this.__name, { args: [value, key] });
    },
    $delete: function (key) {
        return createTask(TransactionType.DELETE, this.__name, { args: [key] });
    },
    $find: function (range) {
        var predicate = [];
        for (var _i = 1; _i < arguments.length; _i++) {
            predicate[_i - 1] = arguments[_i];
        }
        return getTransactionCursorHandler(this, range, predicate);
    },
};
var i = IDBKeyRange;
var rangeBuilder = {
    equal: i.only,
    upper: i.upperBound,
    lower: i.lowerBound,
    bound: i.bound,
};
var BoxBuilder = /** @class */ (function () {
    function BoxBuilder(tx) {
        this.proto = { tx: tx, $: transactionExecuter, pass: schemaValidator, data: createBoxData };
    }
    /**
     * Create new box
     *
     * @param storeName Object store name
     * @param schema Data schema
     */
    BoxBuilder.prototype.build = function (targetVersion, storeName, schema) {
        var Model = function Box(initalData) {
            // Data validate if initial data is provided
            initalData && this.pass(initalData);
            // Create empty(null) object or box data based on initialData
            return this.data(initalData);
        };
        var context = Object.create(this.proto);
        context.__name = storeName;
        context.__schema = schema;
        context.__version = targetVersion;
        // Handlers
        var handler = Object.assign(context, boxHandler, boxTask);
        Object.setPrototypeOf(Model, handler);
        Object.setPrototypeOf(Model.prototype, context);
        return Model;
    };
    return BoxBuilder;
}());

var BoxDB = /** @class */ (function () {
    /**
     * @constructor
     * @param databaseName IDB name
     * @param version IDB version
     */
    function BoxDB(databaseName, version) {
        this.metas = {};
        this.idb = null;
        this.ready = false;
        this.name = databaseName;
        this.version = version;
        this.tx = new BoxTransaction();
        this.builder = new BoxBuilder(this.tx);
    }
    BoxDB.prototype.getDB = function () {
        return this.idb;
    };
    BoxDB.prototype.getName = function () {
        return this.name;
    };
    BoxDB.prototype.getVersion = function () {
        return this.version;
    };
    BoxDB.prototype.isReady = function () {
        return this.ready;
    };
    /**
     * Returns interrupt task for abort transaction
     */
    BoxDB.interrupt = function () {
        return createTask(TransactionType.INTERRUPT, ''); // empty object store name
    };
    /**
     * Create/update object stores and open IDB
     */
    BoxDB.prototype.open = function () {
        var _this = this;
        return new Promise(function (resolve, reject) {
            var openRequest = self.indexedDB.open(_this.name, _this.version);
            var close = function () {
                openRequest.result && openRequest.result.close();
            };
            openRequest.onsuccess = function (event) {
                _this.ready = true;
                _this.idb = openRequest.result;
                _this.tx.init(openRequest.result);
                resolve(event);
            };
            openRequest.onupgradeneeded = function () {
                try {
                    _this.update(openRequest);
                }
                catch (e) {
                    close();
                    reject(e);
                }
            };
            openRequest.onblocked = function () {
                reject(new BoxDBError('Can not upgrade because the database is already opened'));
            };
            openRequest.onerror = function (event) {
                close();
                reject(event);
            };
        });
    };
    /**
     * Create new box (model)
     *
     * @param storeName object store name
     * @param schema model schema
     * @param options box options
     */
    BoxDB.prototype.box = function (storeName, schema, options) {
        if (this.ready) {
            throw new BoxDBError('Cannot define box after database opened');
        }
        if (!(options === null || options === void 0 ? void 0 : options.force) && this.metas[storeName]) {
            throw new BoxDBError(storeName + ' box already defined');
        }
        this.metas[storeName] = this.toMeta(storeName, schema, options);
        return this.builder.build(this.version, storeName, schema);
    };
    /**
     * Execute mutiple tasks in single transaction
     *
     * @param tasks Transaction tasks
     */
    BoxDB.prototype.transaction = function () {
        var _a;
        var tasks = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            tasks[_i] = arguments[_i];
        }
        return (_a = this.tx).run.apply(_a, tasks).then(function () { return void 0; });
    };
    /**
     * Close database connection
     */
    BoxDB.prototype.close = function () {
        if (!this.ready || !this.idb) {
            throw new BoxDBError('Database not ready');
        }
        this.tx.close();
        this.idb.close();
        this.ready = false;
    };
    /**
     * Create BoxMeta object
     *
     * @param name
     * @param schema
     * @param keyPath
     * @param autoIncrement
     * @param index
     * @param force
     * @returns
     */
    BoxDB.prototype.meta = function (name, schema, inKey, outKey, index, force) {
        return { name: name, schema: schema, inKey: inKey, outKey: outKey, index: index, force: force };
    };
    /**
     * Extract metadata from IDBObjectStore
     *
     * @param objectStore object store
     */
    BoxDB.prototype.convert = function (objectStore) {
        return this.meta(objectStore.name, null, objectStore.keyPath, // assertion (origin: string[] | string)
        objectStore.autoIncrement, Array.from(objectStore.indexNames).map(function (name) {
            var idx = objectStore.index(name);
            return { keyPath: idx.keyPath, unique: idx.unique };
        }), false);
    };
    /**
     * Box schema object convert to BoxMeta
     *
     * @param storeName object store name
     * @param schema box schema
     * @param options box option
     */
    BoxDB.prototype.toMeta = function (storeName, schema, options) {
        var primaryKeyPath = null;
        var indexList = [];
        var configuredSchema = Object.entries(schema).reduce(function (prev, _a) {
            var field = _a[0], type = _a[1];
            // Is BoxDataTypes
            if (typeof type === 'string') {
                prev[field] = { type: type };
            }
            else {
                // Is ConfiguredType
                // If this field use as keyPath(in-line key) for object store
                if (type.key && primaryKeyPath) {
                    // info: not available multiple in-line-key in bxd
                    throw new BoxDBError('Cannot define mutiple in-line-key: ' + storeName);
                }
                // Set this field as in-line-key
                type.key && (primaryKeyPath = field);
                if (type.unique && !type.index) {
                    throw new BoxDBError('unique option requires index option');
                }
                // If this field configured for using index
                type.index && indexList.push({ keyPath: field, unique: !!type.unique });
                prev[field] = type;
            }
            return prev;
        }, {});
        return this.meta(storeName, configuredSchema, primaryKeyPath, !!(options === null || options === void 0 ? void 0 : options.autoIncrement), indexList, !!(options === null || options === void 0 ? void 0 : options.force));
    };
    /**
     * Update object stores
     *
     * @param openRequest IDBOpenRequest
     */
    BoxDB.prototype.update = function (openRequest) {
        var _this = this;
        var db = openRequest.result;
        var tx = openRequest.transaction;
        /* istanbul ignore next */
        if (!tx)
            return;
        // Object store names in IDB
        var objectStoreNames = Array.from(db.objectStoreNames);
        // defined box(object store) names
        var boxStoreNames = Object.keys(this.metas);
        // Helper function that get metadata of defined box
        var getBoxMeta = function (name) { return _this.metas[name]; };
        objectStoreNames.forEach(function (name, idx) {
            // Update exist object store
            if (boxStoreNames.includes(name)) {
                var _a = getBoxMeta(name), inKey = _a.inKey, outKey = _a.outKey, index_1 = _a.index, force = _a.force;
                var objectStore_1 = tx.objectStore(name);
                var objectStoreMeta = _this.convert(objectStore_1);
                // Delete exist object store
                if (force) {
                    db.deleteObjectStore(name);
                    objectStoreNames.splice(idx, 1); // This object store will be created
                    return;
                }
                if (objectStoreMeta.inKey !== inKey) {
                    throw new BoxDBError('In-line-key cannot be changed: ' + name);
                }
                if (objectStoreMeta.outKey !== outKey) {
                    throw new BoxDBError('Out-of-line-key cannot be changed: ' + name);
                }
                // Update indexes
                var getKeyPath = function (indexConfig) { return indexConfig.keyPath; };
                var idbKeyPaths_1 = objectStoreMeta.index.map(getKeyPath);
                var boxKeyPaths_1 = index_1.map(getKeyPath);
                // (1/3) Update unique option of index
                objectStoreMeta.index.forEach(function (objectStoreIndex) {
                    var boxIndex = index_1.find(function (_a) {
                        var keyPath = _a.keyPath;
                        return keyPath === objectStoreIndex.keyPath;
                    });
                    var originKeyPath = objectStoreIndex.keyPath;
                    // Index option updated
                    if (boxIndex && objectStoreIndex.unique !== boxIndex.unique) {
                        // Change unique option true -> false is available
                        if (objectStoreIndex.unique === true) {
                            // Delete exist index and re-create without unique option
                            objectStore_1.deleteIndex(originKeyPath);
                            objectStore_1.createIndex(originKeyPath, originKeyPath);
                        }
                        else {
                            throw new BoxDBError('unique option cannot be changed to true: ' + originKeyPath);
                        }
                    }
                });
                // (2/3) Delete index if index not found in schema
                idbKeyPaths_1.forEach(function (keyPath) {
                    !boxKeyPaths_1.includes(keyPath) && objectStore_1.deleteIndex(keyPath);
                });
                // (3/3) Create new index if index not exist in object store
                index_1.forEach(function (_a) {
                    var keyPath = _a.keyPath, unique = _a.unique;
                    !idbKeyPaths_1.includes(keyPath) && objectStore_1.createIndex(keyPath, keyPath, { unique: unique });
                });
            }
            else {
                // Delete object store (box not defined)
                db.deleteObjectStore(name);
            }
        });
        // Create new object stores
        boxStoreNames
            .filter(function (name) { return !objectStoreNames.includes(name); })
            .forEach(function (name) {
            var _a = getBoxMeta(name), inKey = _a.inKey, outKey = _a.outKey, index = _a.index;
            var objectStore = db.createObjectStore(name, __assign(__assign({}, (inKey ? { keyPath: inKey } : null)), { autoIncrement: outKey }));
            index.forEach(function (_a) {
                var keyPath = _a.keyPath, unique = _a.unique;
                return objectStore.createIndex(keyPath, keyPath, { unique: unique });
            });
        });
    };
    BoxDB.Types = BoxDataTypes;
    BoxDB.Order = BoxCursorDirections;
    BoxDB.Range = rangeBuilder;
    return BoxDB;
}());

export default BoxDB;
export { BoxCursorDirections, BoxDBError, BoxDataTypes, TransactionMode, TransactionType };
